/**
 * LiveJobStatus — realtime job status indicator.
 *
 * Uses the useRealtime hook to subscribe to job:status events for a
 * specific job. Shows a pulsing "Live" badge when connected and
 * auto-refreshes the job data when a status update arrives.
 *
 * Place this on the job-detail page or track page.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtime } from "@/lib/realtime";
import { Badge } from "@/components/ui/badge";
import { Wifi, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export function LiveJobStatus({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { isConnected, joinJob, leaveJob, onJobStatus, onJobLocation } = useRealtime();
  const joinedRef = useRef(false);

  useEffect(() => {
    if (!isConnected) return;
    joinJob(jobId);
    joinedRef.current = true;

    const offStatus = onJobStatus((data) => {
      if (data.jobId !== jobId) return;
      // Invalidate the job query so the UI refetches with the new status.
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["chat", jobId] });
    });

    const offLoc = onJobLocation((data) => {
      if (data.jobId !== jobId) return;
      // Update a query cache key that the track page can read.
      qc.setQueryData(["job-location", jobId], data);
    });

    return () => {
      offStatus();
      offLoc();
      if (joinedRef.current) {
        leaveJob(jobId);
        joinedRef.current = false;
      }
    };
  }, [isConnected, jobId, joinJob, leaveJob, onJobStatus, onJobLocation, qc]);

  return (
    <Badge
      variant={isConnected ? "default" : "secondary"}
      className="gap-1 text-xs"
    >
      {isConnected ? (
        <>
          <Wifi className="h-3 w-3 animate-pulse" />
          {t("track.live") || "Live"}
        </>
      ) : (
        <>
          <WifiOff className="h-3 w-3" />
          {t("track.offline") || "Offline"}
        </>
      )}
    </Badge>
  );
}

/**
 * LiveNotifications — invisible component that subscribes to the user's
 * personal notification channel and invalidates the notification query
 * whenever a new notification arrives. Place this once in the AppShell.
 */
export function LiveNotifications() {
  const qc = useQueryClient();
  const { isConnected, onNotification } = useRealtime();

  useEffect(() => {
    if (!isConnected) return;
    const off = onNotification(() => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    });
    return off;
  }, [isConnected, onNotification, qc]);

  return null;
}
